import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { NETWORK_SEGMENTS_PATH, NetworkAssemblyControl } from './NetworkAssemblyControl'

/**
 * A network asset's nesting (spec 2026-10-20-35). A building or site network
 * lists its floor segments, each opening its floor's plan focused on it; a
 * floor segment shows the building network it is part of, changed from here.
 */
export function NetworkSegmentsCard({ org, assetUid }: { org: string; assetUid: string }) {
    const { t } = useTranslation('assets')
    const { data } = $api.useQuery('get', NETWORK_SEGMENTS_PATH, {
        params: { path: { orgCode: org, assetUid } },
    })
    if (!data) return null
    const isSegment = data.network.level === 'floor'
    if (!isSegment && data.segments.length === 0 && !data.network.level) return null

    return (
        <Card data-testid="network-segments">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{t(isSegment ? 'network_segments.title_segment' : 'network_segments.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {isSegment && (
                    <div className="space-y-1">
                        {data.assembly && (
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org, assetUid: data.assembly.uid }}
                                className="text-sm font-medium text-primary hover:underline"
                                data-testid="network-segments-assembly"
                            >
                                {data.assembly.name || data.assembly.uid}
                            </Link>
                        )}
                        <NetworkAssemblyControl
                            org={org}
                            assetUid={assetUid}
                            selectTestId="network-assembly-select"
                            promoteTestId="network-assembly-promote"
                        />
                    </div>
                )}
                {!isSegment && (data.segments.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="network-segments-empty">{t('network_segments.empty')}</p>
                ) : (
                    <ul className="divide-y divide-border">
                        {data.segments.map((s) => (
                            <li key={s.uid} className="flex items-center gap-2 py-2" data-testid={`network-segment-${s.uid}`}>
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: s.uid }}
                                    className="truncate text-sm font-medium text-foreground hover:text-primary hover:underline"
                                    data-testid={`network-segment-link-${s.uid}`}
                                >
                                    {s.name || s.uid}
                                </Link>
                                <Link
                                    to="/$org/assets/$assetUid/plan-editor"
                                    params={{ org, assetUid: s.parent_uid }}
                                    search={{ focus: s.uid }}
                                    title={t('network_segments.open_plan')}
                                    className="ml-auto shrink-0 text-xs text-primary hover:underline"
                                    data-testid={`network-segment-plan-${s.uid}`}
                                >
                                    {s.parent_name || s.parent_uid}
                                </Link>
                            </li>
                        ))}
                    </ul>
                ))}
            </CardContent>
        </Card>
    )
}
